import { ReactElement, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useElementHistory } from '@hooks'
import { AxiosError } from 'axios'
import { find } from 'lodash-es'
import { Tooltip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useEditElementStore, useModelStore, roofStoreyKey } from '@editorStores'
import { useBlockScene, useElementLabel, useResultsInvalidation } from '@editorHooks'
import { CollapsibleSettingsBoxSection } from '@editorComponents'
import { useStructuralPlanningFormEsc } from '@structuralPlanningHooks'
import { getModel, getVerticalTransmissionGraph, getProjectFiles, getElementLoads } from '@queries'
import { createVerticalSlab, deleteVerticalSlab, updateVerticalSlab } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import { documentationRootGuid } from 'src/constants/files'
import PlanSettingsForm from '../../../PlanSettingsForm'
import { RectangularShapeForm } from '../../../RectangularShape'
import SplitVerticalSlabLikeForm from '../../../SplitVerticalSlabLikeForm'

interface Props {
  selectedElement: string
  geometryEditable?: boolean
  activeStorey?: string
  /**
   * Whether to show toggle to change drawing mode
   */
  showDrawSlabOnElement?: boolean
  onClose: () => void
}

const VerticalSlabsForm = ({
  selectedElement,
  geometryEditable = true,
  activeStorey,
  showDrawSlabOnElement = false,
  onClose,
}: Props): ReactElement | null => {
  const drawSlabOnElement = useEditElementStore(state => state.drawSlabOnElement)
  const setDrawSlabOnElement = useEditElementStore(state => state.setDrawSlabOnElement)

  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const removeSlab = useModelStore(state => state.removeVerticalSlab)
  const updateSlab = useModelStore(state => state.updateVerticalSlab)
  const updateStorey = useModelStore(state => state.updateStorey)

  const removeVerticalSlab = useModelStore(state => state.removeVerticalSlab)
  const setTypeVisibility = useModelStore(state => state.setTypeVisibility)

  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()
  const client = useQueryClient()

  const invalidateResults = useResultsInvalidation()

  const getLabel = useElementLabel()

  const verticalSlab = useMemo(() => {
    return find(verticalSlabs, ['guid', selectedElement]) as VerticalSlab
  }, [verticalSlabs, selectedElement])

  const updateVerticalSlabM = useModelStore(state => state.updateVerticalSlab)

  const model = useModelStore(state => state.model)

  const storeys = useMemo(() => [...Object.keys(model.storey_boundaries), roofStoreyKey], [model])

  useEffect(() => {
    // remove vertical roof slab when component unmounts
    // when vertical roof slab.is_local is still true
    // this means that the element was not saved
    return () => {
      const verticalSlabsInState = useModelStore.getState().model.vertical_slabs
      const selectedVerticalSlabInState = find(verticalSlabsInState, ['guid', selectedElement])

      // slab has to be saved otherwise remove
      if (selectedVerticalSlabInState?.is_local)
        removeVerticalSlab(selectedVerticalSlabInState.guid)
    }
  }, [removeVerticalSlab, selectedElement])

  useStructuralPlanningFormEsc(() => {
    const slabs = useModelStore.getState().model.vertical_slabs
    const roof = find(slabs, ['guid', selectedElement])
    // slab has to be saved otherwise remove
    if (roof?.is_local) {
      removeVerticalSlab(roof.guid)
    }
    onClose()
  }, !!selectedElement)

  // mutations

  const { mutateAsync: handleCreate, isLoading: isCreating } = useMutation(
    (slab: ShapeObject) => createVerticalSlab.request(projectId, slab),
    {
      onSuccess: async (data: ShapeObject) => {
        setSlabStale(data)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getElementLoads.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Decke erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        const errorMessage = buildErrorMessage(error, 'Fehler beim Speichern der Decke')
        enqueueSnackbar(errorMessage, { variant: 'error' })
      },
    },
  )

  const { mutateAsync: mutateVerticalSlab, isLoading: isUpdating } = useMutation(
    async (slab: ShapeObject) => {
      if (slab.element_type === 'vertical_slabs') {
        const slabWithMetadata = slab as VerticalSlab
        slabWithMetadata.metadata = slabWithMetadata.metadata ?? verticalSlab?.metadata
      }

      return updateVerticalSlab.request(projectId, slab)
    },
    {
      onSuccess: async (data: ShapeObject) => {
        setSlabStale(data)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getElementLoads.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Decke erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        const errorMessage = buildErrorMessage(error, 'Fehler beim Speichern der Decke')
        enqueueSnackbar(errorMessage, { variant: 'error' })
      },
    },
  )
  const handleUpdate = async (slab: ShapeObject) => mutateVerticalSlab(slab)
  const { mutateAsync: handleDelete, isLoading: isDeleting } = useMutation(
    (slabGuid: string) => deleteVerticalSlab.request(projectId, slabGuid),
    {
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getProjectFiles.getKey(projectId, documentationRootGuid)),
          client.invalidateQueries(getElementLoads.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Decke erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        const errorMessage = buildErrorMessage(error, 'Fehler beim Löschen der Decke')
        enqueueSnackbar(errorMessage, { variant: 'error' })
      },
    },
  )

  const { setStale: setSlabStale } = useElementHistory({
    element: verticalSlab,
    getCurrentElement: () => {
      const slabs = useModelStore.getState().model.vertical_slabs
      return find(slabs, ['guid', selectedElement]) as ShapeObject
    },
    removeElement: slab => removeSlab(slab.guid),
    resetElement: slab => updateSlab(slab),
    dependencies: [selectedElement],
  })

  useBlockScene(isCreating || isDeleting || isUpdating)

  useEffect(() => {
    setTypeVisibility('vertical_slabs' as ElementTypes, true)
  }, [setTypeVisibility])

  const ElementSwitch = () =>
    showDrawSlabOnElement ? (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ m: 2 }}>
        <Tooltip title="Wenn diese Einstellung auf 'auf Element zeichnen' steht, wird das Element auf welchem die Maus bei Starten des Zeichnens ruht als Referenzelement für die Zeichenebene genutzt. Andernfalls wird das aktive Stockwerk als Referenz verwendet. ">
          <ToggleButtonGroup
            value={drawSlabOnElement}
            exclusive
            onChange={(_, value) => {
              // when the value is null, it means that the toggle button is not selected
              // this leads to the effect that an already enabled button stays enabled
              value !== null && setDrawSlabOnElement(value)
            }}
            fullWidth={true}
          >
            <ToggleButton
              value={false}
              aria-label="is horizontal purlin"
              fullWidth={true}
              sx={{ paddingY: 0 }}
            >
              <Typography>Auf Stockwerk zeichnen</Typography>
            </ToggleButton>
            <ToggleButton
              value={true}
              aria-label="is non horizontal rafter"
              fullWidth={true}
              sx={{ paddingY: 0 }}
            >
              <Typography>Auf Element zeichnen</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
      </Stack>
    ) : (
      <></>
    )

  if (!verticalSlab) return <ElementSwitch />

  return (
    <>
      <ElementSwitch />

      <RectangularShapeForm
        element={verticalSlab}
        onCreate={handleCreate}
        onDelete={handleDelete}
        onUpdate={handleUpdate}
        onDeleteLocal={removeVerticalSlab}
        initialTab={'geometry'}
        label={getLabel(selectedElement)}
        geometryEditable={geometryEditable}
        isDeleting={isDeleting}
        updateShape={updateVerticalSlabM}
        storeys={storeys}
        updateStorey={updateStorey}
        activeStorey={activeStorey}
        isSubmitting={isCreating || isUpdating}
        onClose={onClose}
        settingsSection={
          <CollapsibleSettingsBoxSection defaultExpanded={true}>
            <PlanSettingsForm element={verticalSlab} setStale={setSlabStale} />
          </CollapsibleSettingsBoxSection>
        }
        splitSection={
          <SplitVerticalSlabLikeForm
            element_guid={selectedElement}
            setSlabStale={setSlabStale}
            handleUpdate={handleUpdate}
            handleCreate={handleCreate}
          />
        }
      />
    </>
  )
}

export default VerticalSlabsForm
